import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Lock } from "lucide-react";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValidSession, setIsValidSession] = useState<boolean | null>(null);
  const [isLovableDomain] = useState(() => window.location.hostname.includes("lovable.app"));

  // Check if there's a valid recovery session
  useEffect(() => {
    const checkSession = async () => {
      // Log the full URL for debugging
      console.log("ResetPassword: Full URL", window.location.href);
      
      // Check URL hash for recovery token first
      const hash = window.location.hash.substring(1);
      const hashParams = new URLSearchParams(hash);
      const accessToken = hashParams.get("access_token");
      const type = hashParams.get("type");
      const refreshToken = hashParams.get("refresh_token");
      
      console.log("ResetPassword: Hash params", { 
        fullHash: hash,
        hasHash: !!hash, 
        hasAccessToken: !!accessToken, 
        type, 
        hasRefreshToken: !!refreshToken,
        allHashParams: Object.fromEntries(hashParams.entries())
      });
      
      // First, check if we already have a valid session (Supabase might have auto-processed the hash)
      let { data: { session } } = await supabase.auth.getSession();
      if (session) {
        console.log("ResetPassword: Found existing session");
        setIsValidSession(true);
        return;
      }
      
      // If we have hash params with recovery token, try to set the session
      if (accessToken && type === "recovery") {
        console.log("ResetPassword: Processing recovery token from hash");
        // Set the session from the hash
        // Note: refresh_token might not always be present in recovery links
        const sessionData: { access_token: string; refresh_token?: string } = {
          access_token: accessToken,
        };
        
        if (refreshToken) {
          sessionData.refresh_token = refreshToken;
        }
        
        const { data, error } = await supabase.auth.setSession(sessionData);
        
        if (error) {
          console.error("ResetPassword: Error setting session", error);
          setIsValidSession(false);
          return;
        }
        
        if (data.session) {
          console.log("ResetPassword: Session set successfully");
          // Clear the hash from URL after processing
          window.history.replaceState(null, "", window.location.pathname);
          setIsValidSession(true);
          return;
        }
      }
      
      // No session found and no valid hash params
      console.log("ResetPassword: No valid session or recovery token found");
      setIsValidSession(false);
    };
    
    checkSession();
  }, []);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      toast({
        title: "Password required",
        description: "Please enter a new password.",
        variant: "destructive",
      });
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }
    
    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });
      
      if (error) {
        toast({
          title: "Password reset failed",
          description: error.message,
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }
      
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated. Redirecting to login...",
      });
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1500);
    } catch (err) {
      toast({
        title: "Password reset failed",
        description: err instanceof Error ? err.message : "An unexpected error occurred",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  if (isValidSession === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>Loading...</p>
      </div>
    );
  }

  if (isValidSession === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Reset Link</CardTitle>
            <CardDescription>
              This password reset link is invalid or has expired.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertDescription>
                Please request a new password reset link from the login page.
              </AlertDescription>
            </Alert>
            <Button
              onClick={() => navigate("/login")}
              className="w-full mt-4"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-8 w-8 text-primary" />
          </div>
          <CardTitle>Reset Your Password</CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
          {isLovableDomain && (
            <Alert className="mt-4" variant="default">
              <AlertDescription className="text-sm">
                <strong>Note:</strong> You were redirected from a temporary link. Please update your Supabase dashboard Site URL to prevent this in the future.
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Updating password..." : "Update Password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;

